import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, Search, Download } from 'lucide-react';
import MainService from '../../services/MainService';
import DataColumnMenu from '../ui/DataColumnMenu';
import { columnHasActiveFilter, groupRows } from '../ui/columnMenuUtils';
import { useGroupCollapse, GroupByBanner, GroupHeaderLabel } from '../ui/groupCollapse';
import { flattenFiltersForApi } from '../../utils/listFilters';
import { PAGE_SIZE_OPTIONS, buildPageItems } from '../../utils/listPagination';
import { exportGroupedExcel } from '../../utils/excelExport';
import { GridText } from '../ui/gridCell';

const todayInput = () => new Date().toISOString().split('T')[0];

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const parseTotal = (response, fallback = 0) => Number(response?.Total ?? response?.total ?? fallback) || 0;

const dateParts = (value) => {
  const date = value ? new Date(`${value}T12:00:00`) : new Date();
  return {
    FromDay: date.getDate(),
    FromMonth: date.getMonth() + 1,
    FromYear: date.getFullYear(),
  };
};

const dateRangeParts = (fromValue, toValue) => {
  const from = fromValue ? new Date(`${fromValue}T12:00:00`) : new Date();
  const to = toValue ? new Date(`${toValue}T12:00:00`) : from;
  return {
    FromDay: from.getDate(),
    FromMonth: from.getMonth() + 1,
    FromYear: from.getFullYear(),
    ToDay: to.getDate(),
    ToMonth: to.getMonth() + 1,
    ToYear: to.getFullYear(),
  };
};

const defaultFilterState = (dateRange) =>
  dateRange
    ? { FromDate: todayInput(), ToDate: todayInput(), Keyword: '', AreaId: '', DriverId: '', StatusId: '' }
    : { Date: todayInput(), Keyword: '', AreaId: '', DriverId: '', StatusId: '' };

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB');
};

const formatAmount = (value) => Number(value || 0).toFixed(2);

const ORDER_COLUMNS = [
  { field: 'voucherno', title: 'Voucher No', headerMinWidth: 110, hideable: true },
  { field: 'clientbill', title: 'Client Bill', headerMinWidth: 100, hideable: true },
  { field: 'drivername', title: 'Driver', headerMinWidth: 120, hideable: true },
  { field: 'drivermob', title: 'Driver Mob', headerMinWidth: 110, hideable: true },
  { field: 'deliverydate', title: 'Delivery Date', headerMinWidth: 120, hideable: true },
  { field: 'productprice', title: 'Product Price', headerMinWidth: 120, hideable: true },
  { field: 'deliverycharge', title: 'Delivery Charge', headerMinWidth: 130, hideable: true },
  { field: 'tax', title: 'Tax', headerMinWidth: 72, hideable: true },
  { field: 'customeramount', title: 'Customer Amount', headerMinWidth: 140, hideable: true },
  { field: 'clientamount', title: 'Client Amount', headerMinWidth: 130, hideable: true },
  { field: 'clientname', title: 'Client', headerMinWidth: 120, hideable: true },
  { field: 'clientmob', title: 'Client Mob', headerMinWidth: 110, hideable: true },
  { field: 'clientemirate', title: 'Client Emirate', headerMinWidth: 120, hideable: true },
  { field: 'clientlocation', title: 'Client Location', headerMinWidth: 130, hideable: true },
  { field: 'customername', title: 'Customer', headerMinWidth: 120, hideable: true },
  { field: 'customermob', title: 'Customer Mob', headerMinWidth: 120, hideable: true },
  { field: 'customeremirate', title: 'Customer Emirate', headerMinWidth: 130, hideable: true },
  { field: 'customerlocation', title: 'Customer Location', headerMinWidth: 140, hideable: true },
  { field: 'attempts', title: 'Attempts', headerMinWidth: 88, hideable: true },
  { field: 'status', title: 'Status', headerMinWidth: 100, hideable: true },
];

const defaultVisibility = () =>
  ORDER_COLUMNS.reduce((acc, col) => {
    acc[col.field] = true;
    return acc;
  }, {});

const getCellValue = (row, field) => {
  switch (field) {
    case 'voucherno':
      return row.VoucherNo || '-';
    case 'clientbill':
      return row.ClientBillNo || '-';
    case 'drivername':
      return row.DriverName || '-';
    case 'drivermob':
      return row.DriverMob || '-';
    case 'deliverydate':
      return formatDate(row.DeliveryDate);
    case 'productprice':
      return formatAmount(row.ProductPrice);
    case 'deliverycharge':
      return formatAmount(row.DeliveryCharge);
    case 'tax':
      return formatAmount(row.TAX);
    case 'customeramount':
      return formatAmount(row.CustomerAmount);
    case 'clientamount':
      return formatAmount(row.ClientAmount);
    case 'clientname':
      return row.Client?.Name || '-';
    case 'clientmob':
      return row.Client?.MobileNo1 || '-';
    case 'clientemirate':
      return row.ClientAddress?.Emirate?.Name || '-';
    case 'clientlocation':
      return row.ClientAddress?.Location?.Name || '-';
    case 'customername':
      return row.Customer?.Name || '-';
    case 'customermob':
      return row.Customer?.MobileNo1 || '-';
    case 'customeremirate':
      return row.CustomerAddress?.Emirate?.Name || '-';
    case 'customerlocation':
      return row.CustomerAddress?.Location?.Name || '-';
    case 'attempts':
      return row.Attempts ?? 0;
    case 'status':
      return row.Status?.Name || '-';
    default:
      return row[field];
  }
};

const OrderFollowupPage = ({ title, endpoint, dateRange = false, requireSearch = false }) => {
  const [rows, setRows] = useState([]);
  const [areas, setAreas] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [filter, setFilter] = useState(() => defaultFilterState(dateRange));
  const [appliedFilter, setAppliedFilter] = useState(() => defaultFilterState(dateRange));
  const [hasSearched, setHasSearched] = useState(!requireSearch);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filtersByField, setFiltersByField] = useState({});
  const [columnVisibility, setColumnVisibility] = useState(defaultVisibility);
  const [groupField, setGroupField] = useState(null);
  const loadSeqRef = useRef(0);
  const pageSizeBeforeGroupRef = useRef(null);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadLookups = useCallback(async () => {
    try {
      const [areaResponse, driverResponse, statusResponse] = await Promise.all([
        MainService.AreasInDropDown(),
        MainService.DriversInDropDown(),
        MainService.StatusInDropDown(),
      ]);
      setAreas(parseList(areaResponse));
      setDrivers(parseList(driverResponse));
      setStatuses(parseList(statusResponse));
    } catch (err) {
      console.error('Load followup lookups error:', err);
      setError('Failed to load filters');
    }
  }, []);

  const fetchRows = useCallback(async ({
    page: pageArg,
    pageSize: pageSizeArg,
    applied: appliedArg,
    sortField: sortFieldArg,
    sortDirection: sortDirectionArg,
    filters: filtersArg,
  } = {}) => {
    const pageNum = pageArg ?? page;
    const size = pageSizeArg !== undefined ? pageSizeArg : pageSize;
    const applied = appliedArg !== undefined ? appliedArg : appliedFilter;
    const sField = sortFieldArg !== undefined ? sortFieldArg : sortField;
    const sDir = sortDirectionArg !== undefined ? sortDirectionArg : sortDirection;
    const filters = filtersArg !== undefined ? filtersArg : filtersByField;
    const seq = ++loadSeqRef.current;
    const isAll = size === 'all';

    try {
      setLoading(true);
      setError('');
      const body = {
        ...(dateRange
          ? dateRangeParts(applied.FromDate, applied.ToDate)
          : dateParts(applied.Date)),
        AreaId: Number(applied.AreaId) || 0,
        DriverId: Number(applied.DriverId) || 0,
        StatusId: Number(applied.StatusId) || 0,
        Keyword: applied.Keyword?.trim() || '',
        Skip: isAll ? 0 : (pageNum - 1) * Number(size),
        Take: isAll ? 0 : Number(size),
        SortField: sField || null,
        SortDir: sDir || 'asc',
        Filters: flattenFiltersForApi(filters),
      };

      const response = await MainService[endpoint](body);
      if (seq !== loadSeqRef.current) return;
      const list = parseList(response);
      setRows(list);
      setTotalCount(parseTotal(response, list.length));
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      console.error(`${endpoint} error:`, err);
      setRows([]);
      setTotalCount(0);
      setError('Network Error');
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [page, pageSize, appliedFilter, sortField, sortDirection, filtersByField, endpoint, dateRange]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    if (!hasSearched) return;
    fetchRows();
  }, [fetchRows, hasSearched]);

  const effectivePageSize = pageSize === 'all' ? Math.max(totalCount, 1) : Number(pageSize);
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(totalCount / effectivePageSize) || 1);
  const pageItems = useMemo(() => buildPageItems(page, totalPages), [page, totalPages]);
  const rangeFrom = totalCount === 0 ? 0 : (pageSize === 'all' ? 1 : (page - 1) * Number(pageSize) + 1);
  const rangeTo = totalCount === 0 ? 0 : (pageSize === 'all' ? totalCount : Math.min(page * Number(pageSize), totalCount));
  const colSpan = useMemo(
    () => ORDER_COLUMNS.filter((c) => columnVisibility[c.field] !== false).length + 1,
    [columnVisibility]
  );
  const activeFilterCount = useMemo(
    () => Object.keys(filtersByField).filter((f) => columnHasActiveFilter(filtersByField, f)).length,
    [filtersByField]
  );

  const displayGroups = useMemo(() => {
    if (!groupField) return [{ key: null, label: null, rows }];
    const mapped = rows.map((r) => ({ ...r, [groupField]: getCellValue(r, groupField) }));
    return groupRows(mapped, groupField);
  }, [rows, groupField]);

  const { isCollapsed, toggleGroup, collapseAll, expandAll } = useGroupCollapse(groupField);

  const groupKeys = useMemo(
    () => displayGroups.filter((g) => g.label != null).map((g) => g.key),
    [displayGroups]
  );

  const updateFilter = (field, value) => {
    setFilter((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilter = () => {
    if (dateRange) {
      if (!filter.FromDate || !filter.ToDate) {
        setError('From date and To date are required');
        return;
      }
      if (filter.FromDate > filter.ToDate) {
        setError('From date cannot be after To date');
        return;
      }
    }
    setError('');
    setHasSearched(true);
    setAppliedFilter(filter);
    setPage(1);
    fetchRows({ page: 1, applied: filter });
  };

  const handleGroupBy = (field) => {
    if (!groupField) {
      pageSizeBeforeGroupRef.current = { pageSize, page };
    }
    setGroupField(field);
    if (pageSize !== 'all') {
      setPage(1);
      setPageSize('all');
    }
  };

  const handleUngroup = () => {
    const prev = pageSizeBeforeGroupRef.current;
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    if (prev) {
      setPageSize(prev.pageSize);
      setPage(prev.page);
    }
  };

  const refresh = () => {
    const next = defaultFilterState(dateRange);
    setFilter(next);
    setAppliedFilter(next);
    setPage(1);
    setSortField('');
    setSortDirection('asc');
    setFiltersByField({});
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    if (requireSearch) {
      setHasSearched(false);
      setRows([]);
      setTotalCount(0);
      setError('');
      return;
    }
    fetchRows({
      page: 1,
      applied: next,
      sortField: '',
      sortDirection: 'asc',
      filters: {},
    });
  };

  const handlePageSizeChange = (value) => {
    const next = value === 'all' ? 'all' : Number(value);
    setPageSize(next);
    setPage(1);
    fetchRows({ page: 1, pageSize: next });
  };

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
    fetchRows({ page: nextPage });
  };

  const handleColumnSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(1);
    fetchRows({ page: 1, sortField: field, sortDirection: direction });
  };

  const handleToggleColumn = (field) => {
    setColumnVisibility((prev) => {
      const nextVisible = { ...prev, [field]: prev[field] === false };
      const stillVisible = ORDER_COLUMNS.some((c) => nextVisible[c.field] !== false);
      if (!stillVisible) return prev;
      return nextVisible;
    });
  };

  const handleApplyColumnFilter = (field, colFilter) => {
    const nextFilters = { ...filtersByField, [field]: colFilter };
    setFiltersByField(nextFilters);
    setPage(1);
    fetchRows({ page: 1, filters: nextFilters });
  };

  const handleClearColumnFilter = (field) => {
    const nextFilters = { ...filtersByField };
    delete nextFilters[field];
    setFiltersByField(nextFilters);
    setPage(1);
    fetchRows({ page: 1, filters: nextFilters });
  };

  const clearSortAndFilters = () => {
    const prev = pageSizeBeforeGroupRef.current;
    setFiltersByField({});
    setSortField('');
    setSortDirection('asc');
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    setPage(1);
    if (prev && prev.pageSize !== pageSize) {
      setPageSize(prev.pageSize);
    }
    fetchRows({
      page: 1,
      pageSize: prev?.pageSize ?? pageSize,
      sortField: '',
      sortDirection: 'asc',
      filters: {},
    });
  };

  const exportToExcel = () => {
    try {
      exportGroupedExcel({
        sheetName: title || 'Orders',
        fileName: `order_followup_${new Date().toISOString().split('T')[0]}.xlsx`,
        headers: ['#', ...ORDER_COLUMNS.map((c) => c.title)],
        groups: displayGroups,
        groupFieldTitle: ORDER_COLUMNS.find((c) => c.field === groupField)?.title || 'Group',
        mapRow: (row, serial) => [
          serial,
          ...ORDER_COLUMNS.map((c) => getCellValue(row, c.field)),
        ],
      });
      setSuccess('Excel file exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      if (err?.message === 'No rows to export') {
        setError('No orders to export');
      } else {
        setError('Failed to export Excel file');
      }
    }
  };

  return (
    <div className="space-y-6">
      {success && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{success}</div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5 border-b border-gray-200 pb-4">
          <h1 className="text-2xl font-bold text-gray-900">
            {title}
            {hasSearched && (
              <>
                {' | '}
                {dateRange
                  ? `${formatDate(appliedFilter.FromDate)} - ${formatDate(appliedFilter.ToDate)}`
                  : formatDate(appliedFilter.Date)}
              </>
            )}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {dateRange
              ? 'Select from date and to date (required), then filter by keyword, area, driver, and status.'
              : 'Filter followup orders by date, keyword, area, driver, and status.'}
          </p>
        </div>

        {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

        <div className={`grid grid-cols-1 gap-4 ${dateRange ? 'md:grid-cols-6' : 'md:grid-cols-5'}`}>
          {dateRange ? (
            <>
              <FilterInput
                label="From Date"
                type="date"
                value={filter.FromDate}
                required
                onChange={(value) => updateFilter('FromDate', value)}
              />
              <FilterInput
                label="To Date"
                type="date"
                value={filter.ToDate}
                required
                onChange={(value) => updateFilter('ToDate', value)}
              />
            </>
          ) : (
            <FilterInput label="Date" type="date" value={filter.Date} onChange={(value) => updateFilter('Date', value)} />
          )}
          <FilterInput label="Keyword" value={filter.Keyword} onChange={(value) => updateFilter('Keyword', value)} onEnter={applyFilter} />
          <FilterSelect label="Area" value={filter.AreaId} onChange={(value) => updateFilter('AreaId', value)} options={areas} />
          <FilterSelect label="Driver" value={filter.DriverId} onChange={(value) => updateFilter('DriverId', value)} options={drivers} />
          <FilterSelect label="Status" value={filter.StatusId} onChange={(value) => updateFilter('StatusId', value)} options={statuses} />
        </div>

        <div className="mt-5 flex justify-center gap-3">
          <button
            type="button"
            onClick={applyFilter}
            disabled={loading || (dateRange && (!filter.FromDate || !filter.ToDate))}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
          >
            <Search className="h-4 w-4" />
            Filter
          </button>
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-2 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title} List</h2>
            <p className="text-sm text-gray-500">
              {totalCount === 0 ? '0 orders' : `${rangeFrom}–${rangeTo} of ${totalCount}`}
            </p>
          </div>
          <button
            type="button"
            onClick={exportToExcel}
            disabled={rows.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>

        <div className="master-table-scroll relative overflow-x-auto" style={{ maxHeight: 560 }}>
          {loading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/75 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-2">
                <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                <span className="text-sm text-gray-600">Loading...</span>
              </div>
            </div>
          )}

          {(activeFilterCount > 0 || sortField) && (
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-[#f0f9f4] px-4 py-2 text-sm text-gray-700">
              <span>
                {sortField
                  ? `Sorted by ${ORDER_COLUMNS.find((c) => c.field === sortField)?.title || sortField} (${sortDirection})`
                  : 'Filters active'}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} column filter(s)` : ''}
              </span>
              <button type="button" onClick={clearSortAndFilters} className="text-primary hover:underline">
                Clear sort & filters
              </button>
            </div>
          )}

          {groupField && (
            <GroupByBanner
              groupTitle={ORDER_COLUMNS.find((c) => c.field === groupField)?.title || groupField}
              groupKeys={groupKeys}
              onUngroup={handleUngroup}
              onCollapseAll={collapseAll}
              onExpandAll={expandAll}
            />
          )}

          <table className="data-grid-table w-full min-w-[2400px]">
            <thead className="data-grid-thead sticky top-0 z-10">
              <tr>
                <th className="data-grid-th data-grid-th-serial w-12 min-w-[3rem] px-3 py-2.5 text-left">#</th>
                {ORDER_COLUMNS.map((column) =>
                  columnVisibility[column.field] === false ? null : (
                    <DataColumnMenu
                      key={column.field}
                      column={column}
                      columns={ORDER_COLUMNS}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleColumnSort}
                      columnVisibility={columnVisibility}
                      onToggleColumn={handleToggleColumn}
                      filtersByField={filtersByField}
                      onApplyFilter={handleApplyColumnFilter}
                      onClearFilter={handleClearColumnFilter}
                      groupField={groupField}
                      onGroupBy={handleGroupBy}
                      onUngroup={handleUngroup}
                    />
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-gray-500">
                    {!hasSearched && requireSearch
                      ? 'Select from date and to date, then click Filter to load orders.'
                      : 'No orders found.'}
                  </td>
                </tr>
              ) : (
                (() => {
                  let serial = 0;
                  return displayGroups.flatMap((group) => {
                    const block = [];
                    if (group.label != null) {
                      block.push(
                        <tr key={`group-${group.key}`} className="bg-gray-100">
                          <td colSpan={colSpan} className="px-4 py-2">
                            <GroupHeaderLabel
                              title={ORDER_COLUMNS.find((c) => c.field === groupField)?.title}
                              label={group.label}
                              count={group.rows.length}
                              collapsed={isCollapsed(group.key)}
                              onToggle={() => toggleGroup(group.key)}
                            />
                          </td>
                        </tr>
                      );
                    }
                    if (group.label != null && isCollapsed(group.key)) {
                      return block;
                    }
                    group.rows.forEach((row, index) => {
                      serial += 1;
                      block.push(
                        <tr key={row.Id || `${row.VoucherNo}-${group.key}-${index}`} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900">
                            {groupField || pageSize === 'all' ? serial : (page - 1) * Number(pageSize) + serial}
                          </td>
                          {ORDER_COLUMNS.map((column) =>
                            columnVisibility[column.field] === false ? null : (
                              <td
                                key={column.field}
                                className={`px-4 py-2 text-sm ${
                                  column.field === 'voucherno' ? 'font-semibold text-gray-900' : 'text-gray-600'
                                }`}
                              >
                                <GridText value={getCellValue(row, column.field)} />
                              </td>
                            )
                          )}
                        </tr>
                      );
                    });
                    return block;
                  });
                })()
              )}
            </tbody>
          </table>
        </div>

        <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
            <div className="flex flex-wrap items-center gap-3">
              <span>
                {totalCount === 0 ? '0 orders' : `${rangeFrom}–${rangeTo} of ${totalCount}`}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} filter(s)` : ''}
                {groupField
                  ? ` · grouped by ${ORDER_COLUMNS.find((c) => c.field === groupField)?.title || groupField}`
                  : ''}
              </span>
              <label className="flex items-center gap-2">
                <span className="text-gray-500">Rows</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                  disabled={loading || !!groupField}
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-[#46955A] focus:outline-none"
                >
                  {PAGE_SIZE_OPTIONS.map((opt) => (
                    <option key={String(opt)} value={opt}>
                      {opt === 'all' ? 'All' : opt}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {!groupField && pageSize !== 'all' && (
              <div className="flex flex-wrap items-center gap-1">
                <button
                  type="button"
                  disabled={page <= 1 || loading}
                  onClick={() => goToPage(page - 1)}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Prev
                </button>
                {pageItems.map((item, idx) =>
                  item === '…' ? (
                    <span key={`e-${idx}`} className="px-1.5 text-gray-400">…</span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      disabled={loading}
                      onClick={() => goToPage(item)}
                      className={`min-w-[2rem] rounded-lg px-2 py-1.5 text-sm transition-colors ${
                        item === page
                          ? 'bg-[#46955A] font-medium text-white'
                          : 'border border-gray-200 bg-white text-gray-700 hover:bg-[#f0f9f4]'
                      }`}
                    >
                      {item}
                    </button>
                  )
                )}
                <button
                  type="button"
                  disabled={page >= totalPages || loading}
                  onClick={() => goToPage(page + 1)}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const FilterInput = ({ label, value, onChange, type = 'text', onEnter, required = false }) => (
  <div>
    <label className="mb-1 block text-sm font-medium text-gray-700">
      {label}
      {required ? <span className="text-red-500"> *</span> : null}
    </label>
    <input
      type={type}
      value={value}
      required={required}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && onEnter) onEnter();
      }}
      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
    />
  </div>
);

const FilterSelect = ({ label, value, onChange, options }) => (
  <div>
    <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
    >
      <option value="">All</option>
      {options.map((option) => (
        <option key={option.Id} value={option.Id}>
          {option.Name}
        </option>
      ))}
    </select>
  </div>
);

export default OrderFollowupPage;
